import React, { useState, useEffect, useRef } from 'react';
import { useClient } from '../context/ClientContext';
import { useLocation } from 'react-router-dom';
import axios from 'axios';
import { Sparkles, X, Send, Bot, User as UserIcon, Loader2 } from 'lucide-react';

const SESSION_KEY = 'epicurea_chat_session';

const PAGE_HINTS = {
  '/dashboard': 'dashboard',
  '/menu': 'menu',
  '/suppliers': 'suppliers',
  '/reconciliation': 'reconciliation',
  '/files': 'files',
  '/clients': 'clients'
};

const SUGGESTED_PROMPTS = {
  dashboard: ['What should I focus on first?', 'Explain my food cost %', 'How is my profit margin?'],
  menu: ['Which items should I promote?', 'How do I improve menu margins?', 'What\'s the Star/Plowhorse logic?'],
  suppliers: ['Top supplier issues?', 'How to negotiate better rates?', 'Should I switch suppliers?'],
  reconciliation: ['Explain card vs bill variance', 'What is settlement fee?', 'Why are deposits mismatched?'],
  'files': ['What files do I have uploaded?', 'How often should I upload new data?', 'What format is best for upload?'],
  clients: ['How to onboard new clients?', 'What data should I collect first?']
};

export const AIAssistant = () => {
  const { selectedClient, API } = useClient();
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [sessionId] = useState(() => {
    let stored = localStorage.getItem(SESSION_KEY);
    if (!stored) {
      stored = `session-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
      localStorage.setItem(SESSION_KEY, stored);
    }
    return stored;
  });
  const messagesEndRef = useRef(null);

  const contextPage = PAGE_HINTS[location.pathname] || 'general';
  const suggestions = SUGGESTED_PROMPTS[contextPage] || SUGGESTED_PROMPTS.dashboard;

  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages]);

  const sendMessage = async (text) => {
    if (!text.trim() || loading) return;

    const userMessage = { role: 'user', content: text };
    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setLoading(true);

    try {
      const response = await axios.post(`${API}/ai/chat`, {
        session_id: sessionId,
        message: text,
        client_id: selectedClient?.id || null,
        context_page: contextPage
      });

      setMessages(prev => [...prev, { role: 'assistant', content: response.data.response }]);
    } catch (err) {
      setMessages(prev => [...prev, { 
        role: 'assistant', 
        content: 'Sorry, I had trouble responding. Please try again.' 
      }]);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    sendMessage(input);
  };

  return (
    <>
      {/* Floating button */}
      {!isOpen && (
        <button
          onClick={() => setIsOpen(true)}
          className="fixed bottom-24 right-6 z-50 bg-accent hover:bg-accent-hover text-white rounded-full p-4 shadow-lg hover:shadow-xl transition-all hover:-translate-y-1 flex items-center space-x-2"
          data-testid="ai-assistant-toggle"
        >
          <Sparkles className="h-6 w-6" />
          <span className="hidden md:inline font-medium pr-2">Ask AI</span>
        </button>
      )}

      {/* Chat panel */}
      {isOpen && (
        <div 
          className="fixed bottom-24 right-6 z-50 bg-white rounded-md shadow-2xl border border-border w-full max-w-md flex flex-col"
          style={{ height: '600px', maxHeight: 'calc(100vh - 48px)' }}
          data-testid="ai-assistant-panel"
        >
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-border bg-primary text-white rounded-t-md">
            <div className="flex items-center space-x-2">
              <Sparkles className="h-5 w-5 text-accent" />
              <div>
                <h3 className="font-semibold text-sm">Epicurea AI Assistant</h3>
                <p className="text-xs opacity-75">
                  {selectedClient ? `Context: ${selectedClient.name}` : 'No client selected'}
                </p>
              </div>
            </div>
            <button 
              onClick={() => setIsOpen(false)}
              className="hover:bg-primary-hover rounded-md p-1"
              data-testid="ai-assistant-close"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-4 space-y-3 bg-background">
            {messages.length === 0 && (
              <div className="text-center py-8" data-testid="ai-empty-state">
                <Bot className="h-12 w-12 text-primary mx-auto mb-3 opacity-50" />
                <p className="text-sm text-text-secondary mb-4">
                  Hi! I'm your AI assistant. Ask me anything about your restaurant data.
                </p>
                <div className="space-y-2">
                  {suggestions.map((s, idx) => (
                    <button
                      key={idx}
                      onClick={() => sendMessage(s)}
                      className="block w-full text-left bg-white border border-border hover:border-primary rounded-md px-3 py-2 text-sm text-text-secondary hover:text-primary transition-colors"
                      data-testid={`suggested-prompt-${idx}`}
                    >
                      💬 {s}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {messages.map((msg, idx) => (
              <div key={idx} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`} data-testid={`message-${idx}`}>
                <div className={`flex items-start space-x-2 max-w-[85%] ${msg.role === 'user' ? 'flex-row-reverse space-x-reverse' : ''}`}>
                  <div className={`rounded-full p-2 flex-shrink-0 ${
                    msg.role === 'user' ? 'bg-primary text-white' : 'bg-accent text-white'
                  }`}>
                    {msg.role === 'user' ? <UserIcon className="h-3 w-3" /> : <Sparkles className="h-3 w-3" />}
                  </div>
                  <div className={`rounded-md p-3 ${
                    msg.role === 'user' 
                      ? 'bg-primary text-white' 
                      : 'bg-white border border-border text-text-primary'
                  }`}>
                    <div className="text-sm whitespace-pre-wrap leading-relaxed">{msg.content}</div>
                  </div>
                </div>
              </div>
            ))}

            {loading && (
              <div className="flex justify-start" data-testid="ai-loading">
                <div className="flex items-center space-x-2 bg-white border border-border rounded-md p-3">
                  <Loader2 className="h-4 w-4 animate-spin text-accent" />
                  <span className="text-sm text-text-secondary">Thinking...</span>
                </div>
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>

          {/* Input */}
          <form onSubmit={handleSubmit} className="p-3 border-t border-border bg-white rounded-b-md">
            <div className="flex space-x-2">
              <input
                type="text"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Ask me anything..."
                className="flex-1 border border-border rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-primary/20 focus:border-primary"
                disabled={loading}
                data-testid="ai-chat-input"
              />
              <button
                type="submit"
                disabled={loading || !input.trim()}
                className="bg-primary text-white hover:bg-primary-hover rounded-md px-4 py-2 disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="ai-chat-send"
              >
                <Send className="h-4 w-4" />
              </button>
            </div>
          </form>
        </div>
      )}
    </>
  );
};
